export const TABLE_STICKY_LAYOUT_DATA_ATTR = 'data-dsys-table-sticky-layout';
export const TABLE_STICKY_SCROLL_CONTAINER_DATA_ATTR = 'data-dsys-table-scroll-container';
/** Table uses page scroll + CSS sticky headers (scrollMode="page"). */
const TABLE_PAGE_SCROLL_STICKY_ATTR = 'data-dsys-table-page-scroll-sticky';

export const tablePageScrollStickyDataProps = {
  [TABLE_PAGE_SCROLL_STICKY_ATTR]: 'true',
} as const;

export const isPageScrollStickyTable = (layoutElement: HTMLElement | null): boolean =>
  Boolean(
    layoutElement?.hasAttribute(TABLE_PAGE_SCROLL_STICKY_ATTR) ||
    layoutElement?.querySelector(`[${TABLE_PAGE_SCROLL_STICKY_ATTR}]`)
  );

/**
 * Page-scroll table stacking: the sticky frame sits with sticky headers, above cells.
 * Layout.Header uses DSYS_FIXED_CHROME_Z_INDEX (4) so chrome stays above edge shadows.
 */
const TABLE_STICKY_HEADER_Z_INDEX = 2;
export const TABLE_PAGE_SCROLL_FRAME_Z_INDEX = 2;

export const GROUP_STICKY_HEADER_Z_INDEX = TABLE_STICKY_HEADER_Z_INDEX;

/** Pixel distance from viewport top to below fixed Layout.Header (for sticky/fixed table headers). */
export const readStickyOffsetTopPx = (): number => {
  if (typeof document === 'undefined') {
    return 52;
  }

  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;visibility:hidden;pointer-events:none;top:var(--dsys-sticky-offset-top, 52px);left:0;width:0;height:0';
  document.body.appendChild(probe);
  const top = probe.getBoundingClientRect().top;
  probe.remove();
  return top;
};

/** Pixel distance from viewport left to the start of the content area (right of the fixed nav). */
export const readMainOffsetLeftPx = (): number => {
  if (typeof document === 'undefined') {
    return 56;
  }

  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;visibility:hidden;pointer-events:none;left:var(--dsys-main-offset-left, 56px);top:0;width:0;height:0';
  document.body.appendChild(probe);
  const left = probe.getBoundingClientRect().left;
  probe.remove();
  return left;
};

export const tableStickyLayoutDataProps = {[TABLE_STICKY_LAYOUT_DATA_ATTR]: 'true'} as const;
export const tableStickyScrollContainerDataProps = {
  [TABLE_STICKY_SCROLL_CONTAINER_DATA_ATTR]: 'true',
} as const;

export const findTableLayoutElement = (from: Element | null): HTMLElement | null => {
  if (!from) return null;
  return (from.closest(`[${TABLE_STICKY_LAYOUT_DATA_ATTR}]`) ??
    // Workstreams v2 table predates TABLE_STICKY_LAYOUT_DATA_ATTR.
    from.closest('[data-workstreams-v2-table-layout]')) as HTMLElement | null;
};

export const getTableWrapperElement = (root: ParentNode | null): HTMLElement | null =>
  (root?.querySelector?.('[class*="tableWrapper"]') as HTMLElement | null) ?? null;

const getTableScrollContainerFromWrapper = (tableWrapper: HTMLElement | null): HTMLElement | null =>
  (tableWrapper?.querySelector?.(':scope > [class*="body"]') as HTMLElement | null) ?? tableWrapper;

const findMarkedScrollContainer = (from: Element | null): HTMLElement | null => {
  let node: HTMLElement | null = from instanceof HTMLElement ? from : null;
  while (node) {
    if (node.hasAttribute(TABLE_STICKY_SCROLL_CONTAINER_DATA_ATTR)) {
      return node;
    }
    if (node.hasAttribute('data-workstreams-v2-table-scroll-container')) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

export const getTableScrollContainerElement = (root: ParentNode | null): HTMLElement | null => {
  if (!root) return null;

  const layoutRoot = root instanceof Element ? findTableLayoutElement(root) : null;
  const searchRoot = layoutRoot ?? root;

  return (
    findMarkedScrollContainer(layoutRoot) ??
    (searchRoot.querySelector?.(
      `[${TABLE_STICKY_SCROLL_CONTAINER_DATA_ATTR}]`
    ) as HTMLElement | null) ??
    // Workstreams v2 table predates TABLE_STICKY_SCROLL_CONTAINER_DATA_ATTR.
    (searchRoot.querySelector?.(
      '[data-workstreams-v2-table-scroll-container]'
    ) as HTMLElement | null) ??
    getTableScrollContainerFromWrapper(getTableWrapperElement(searchRoot))
  );
};

const canElementScrollVertically = (element: HTMLElement) => {
  const {overflowY} = getComputedStyle(element);
  if (!/(auto|scroll|overlay)/.test(overflowY)) {
    return false;
  }
  return element.scrollHeight > element.clientHeight + 1;
};

/** Nearest ancestor (or window) that vertically scrolls table content. */
const getStickyScrollTarget = (
  layoutElement: HTMLElement | null,
  tableContainer: HTMLElement
): HTMLElement | Window => {
  if (canElementScrollVertically(tableContainer)) {
    return tableContainer;
  }

  let node = layoutElement?.parentElement ?? tableContainer.parentElement;
  while (node) {
    if (canElementScrollVertically(node)) {
      return node;
    }
    node = node.parentElement;
  }

  return window;
};

export const observeStickyLayout = ({
  observedElements,
  onLayoutChange,
  tableContainer,
  scrollTarget,
}: {
  observedElements: Array<HTMLElement | null | undefined>;
  onLayoutChange: () => void;
  tableContainer: HTMLElement;
  scrollTarget?: HTMLElement | Window;
}) => {
  const resolvedScrollTarget =
    scrollTarget ?? getStickyScrollTarget(findTableLayoutElement(tableContainer), tableContainer);

  if (resolvedScrollTarget === window) {
    window.addEventListener('scroll', onLayoutChange, {passive: true});
  } else {
    resolvedScrollTarget.addEventListener('scroll', onLayoutChange, {passive: true});
  }

  window.addEventListener('resize', onLayoutChange, {passive: true});

  const elementsToObserve = Array.from(
    new Set(observedElements.filter((element): element is HTMLElement => element != null))
  );
  const resizeObserver =
    typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(() => {
          onLayoutChange();
        });

  elementsToObserve.forEach((element) => resizeObserver?.observe(element));
  onLayoutChange();

  return () => {
    if (resolvedScrollTarget === window) {
      window.removeEventListener('scroll', onLayoutChange);
    } else {
      resolvedScrollTarget.removeEventListener('scroll', onLayoutChange);
    }
    window.removeEventListener('resize', onLayoutChange);
    resizeObserver?.disconnect();
  };
};
